<template>
  <div>
    <h1 :id="name">{{ name }}</h1>
    <FeatureViewer
      ref="featureview"
      v-bind:sequence="sequence"
      v-bind:features="features"
      v-bind:visible="visible"
      v-bind:arrow-width="arrowWidth"
      :show-sequence="false"
      :single-line="true"
    ></FeatureViewer>
    <HomologyTable v-bind:hits="hits" :dbxrefs="dbxrefs"></HomologyTable>
  </div>
</template>

<script>
import d3 from "d3";
import FeatureViewer from "@/components/FeatureViewer.vue";
import HomologyTable from "@/components/HomologyTable.vue";

export default {
  name: "HomologyViewer",
  components: { FeatureViewer, HomologyTable },
  data: function() {
    return {
      colorid: 1,
      filterid: 0,
      sortid: 0,
      filters: [
        {
          name: "all",
          // eslint-disable-next-line no-unused-vars
          func: function(d) {
            return true;
          }
        },
        {
          name: "evalue",
          func: function(d) {
            return d.target.evalue < "1e-10";
          }
        }
      ],
      colors: [
        {
          name: "evalue",
          // eslint-disable-next-line no-unused-vars
          range: function(all) {
            return [0, 0.000001];
          },
          value: function(d) {
            return d.target.evalue;
          }
        },
        {
          name: "score",
          range: function(all) {
            let scores = all.map(function(x) {
              return x.target.score;
            });
            return [Math.min(...scores), Math.max(...scores)];
          },
          value: function(d) {
            return d.target.score;
          }
        },
        {
          name: "percent identity",
          // eslint-disable-next-line no-unused-vars
          range: function(all) {
            return [0, 100];
          },
          value: function(d) {
            return d.target.score;
          }
        }
      ],
      sorts: [
        {
          name: "evalue",
          func: function(a, b) {
            return a.evalue - b.evalue;
          }
        },
        {
          name: "score",
          func: function(a, b) {
            return b.score - a.score;
          }
        },
        {
          name: "percent identity",
          func: function(a, b) {
            return b.percent_identity - a.percent_identity;
          }
        }
      ]
    };
  },
  props: ["sequence", "visible", "name", "arrowWidth", "hits", "dbxrefs"],
  computed: {
    color() {
      return this.colors[this.colorid];
    },
    filter() {
      return this.filters[this.filterid].func;
    },
    sortf() {
      return this.sorts[this.sortid].func;
    },
    features() {
      let vm = this;
      let domain_range = vm.color.range(vm.hits);
      var scale = d3.scale
        .linear()
        .domain(domain_range)
        .range(["#FFBEBC", "#6EB5FF"]);

      // collect hits for same database entry
      let idx = {};
      for (let r of this.hits) {
        if (!(r.target.dbxref in idx)) {
          idx[r.target.dbxref] = [];
        }
        idx[r.target.dbxref].push(r);
      }

      // for each database entry generate a feature
      let features = [];
      let feature_id = 0;
      for (let key of Object.keys(idx)) {
        let results = idx[key];
        let data = [];
        for (let result of results) {
          let description = null;
          if (
            "description" in result.target &&
            result.target.description !== "-"
          ) {
            description = result.target.description;
          } else if ("name" in result.target) {
            description = result.target.name;
          } else {
            description = result.target.dbxref;
          }
          let id = "homology_" + feature_id++;
          if (this.filter(result)) {
            data.push({
              data: result,
              score: result.target.score,
              x: result.query.start,
              y: result.query.end,
              description: description,
              id: id,
              tooltip: this.format_homology_tooltip(result),
              color: scale(vm.color.value(result))
            });
          }
        }
        if (data.length > 0) {
          features.push({
            data: data,
            name: key,
            className: this.name,
            color: "#afd0e4",
            type: "rect",
            sort: vm.sortf,
            separateLines: true
          });
        }
      }
      return features;
    }
  },
  methods: {
    format_homology_tooltip: function(result) {
      let html = [];
      html.push("<div>");
      html.push("<p><b>Target:</b> " + result.target.dbxref + "</p>");

      if ("start" in result.target && "end" in result.target) {
        html.push(
          "<p><b>Target location:</b> " +
            result.target.start +
            "-" +
            result.target.end +
            "</p>"
        );
      }
      if ("start" in result.query && "end" in result.query) {
        html.push(
          "<p><b>Query location:</b> " +
            result.query.start +
            "-" +
            result.query.end +
            "</p>"
        );
      }
      if ("score" in result.target) {
        html.push("<p><b>Score:</b> " + result.target.score + "</p>");
      }
      if ("percent_identity" in result.target) {
        html.push(
          "<p><b>Percent identity:</b> " +
            result.target.percent_identity +
            "</p>"
        );
      }
      if ("evalue" in result.target) {
        html.push("<p><b>E-value:</b> " + result.target.evalue + "</p>");
      }
      html.push("</div>");
      return html.join("");
    }
  }
};
</script>
